import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, CircularProgress, Stack } from '@mui/material';

interface Props {
  onImage: (file: File) => void;
  onBack: () => void;
}

export default function CameraCapture({ onImage, onBack }: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const liveRef = useRef(false);
  const generationRef = useRef(0);
  const [attempt, setAttempt] = useState(0);
  const [ready, setReady] = useState(false);
  const [capturing, setCapturing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    let stream: MediaStream | undefined;
    liveRef.current = true;
    generationRef.current += 1;
    setCapturing(false);
    setReady(false);
    setError(null);
    const stop = () => stream?.getTracks().forEach((track) => track.stop());
    const start = async () => {
      try {
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error('Camera unavailable');
        }
        stream = await navigator.mediaDevices.getUserMedia({
          audio: false,
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1920 },
            height: { ideal: 1080 },
          },
        });
        if (cancelled) {
          stop();
          return;
        }
        const video = videoRef.current;
        if (video) {
          video.srcObject = stream;
          await video.play();
          if (!cancelled)
            setReady(video.videoWidth > 0 && video.videoHeight > 0);
        }
      } catch (cause) {
        stop();
        if (!cancelled)
          setError(
            cause instanceof DOMException && cause.name === 'NotAllowedError'
              ? 'Camera access was denied. Allow camera access and try again, or choose an existing image.'
              : 'The camera could not be opened. Try again or choose an existing image.',
          );
      }
    };
    const pause = () => {
      if (document.visibilityState !== 'hidden') return;
      cancelled = true;
      liveRef.current = false;
      stop();
      setReady(false);
      setError(
        'Camera paused while the app was in the background. Try again to resume.',
      );
    };
    void start();
    document.addEventListener('visibilitychange', pause);
    return () => {
      cancelled = true;
      liveRef.current = false;
      stop();
      document.removeEventListener('visibilitychange', pause);
    };
  }, [attempt]);

  const capture = () => {
    const video = videoRef.current;
    if (!ready || capturing || !video?.videoWidth || !video.videoHeight) return;
    setCapturing(true);
    const generation = generationRef.current;
    try {
      const canvas = document.createElement('canvas');
      const size = Math.min(video.videoWidth, video.videoHeight);
      canvas.width = size;
      canvas.height = size;
      const context = canvas.getContext('2d');
      if (!context) throw new Error('Capture unavailable');
      context.drawImage(
        video,
        (video.videoWidth - size) / 2,
        (video.videoHeight - size) / 2,
        size,
        size,
        0,
        0,
        size,
        size,
      );
      canvas.toBlob((blob) => {
        if (!liveRef.current || generation !== generationRef.current) return;
        setCapturing(false);
        if (blob)
          onImage(new File([blob], 'camera.png', { type: 'image/png' }));
        else setError('The photo could not be captured. Please try again.');
      }, 'image/png');
    } catch {
      setCapturing(false);
      setError('The photo could not be captured. Please try again.');
    }
  };

  return (
    <Stack sx={{ flex: 1, minHeight: 0, p: 2 }} spacing={2}>
      {error && (
        <Alert
          severity="warning"
          action={
            <Button onClick={() => setAttempt((value) => value + 1)}>
              Try again
            </Button>
          }
        >
          {error}
        </Alert>
      )}
      <Box
        sx={{
          width: 'min(100%, calc(100dvh - 180px))',
          aspectRatio: '1 / 1',
          alignSelf: 'center',
          flexShrink: 0,
          position: 'relative',
          bgcolor: 'black',
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          component="video"
          ref={videoRef}
          aria-label="Camera preview"
          autoPlay
          muted
          playsInline
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {!ready && !error && (
          <CircularProgress
            aria-label="Starting camera"
            sx={{ position: 'absolute', top: '50%', left: '50%' }}
          />
        )}
      </Box>
      <Stack direction="row" spacing={2} justifyContent="center">
        <Button onClick={onBack}>Back to image options</Button>
        <Button
          variant="contained"
          onClick={capture}
          disabled={!ready || capturing}
        >
          Capture photo
        </Button>
      </Stack>
    </Stack>
  );
}
